import { useIsMobile } from '@brunommsilva/react-foundations';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Fab } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAssistantPinned from '../../hooks/assistant/use-assistant-pinned';
import useConversation from '../../hooks/assistant/use-conversation';
import useLayout from '../../hooks/layout/use-layout';
import AssistantChat from './conversation/assistant-chat';
import AssistantPanel from './conversation/assistant-panel';
import { gradient, pulse } from './conversation/assistant-theme';

export default function AssistantWidget() {
  const { t } = useTranslation();
  const { isLoading, isUnavailable } = useConversation();
  const isMobile = useIsMobile();
  const { isPinned, setPinned } = useAssistantPinned();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // On a phone the assistant is a page of its own.
  const isHidden = isMobile || isLoading || isUnavailable;
  const isDocked = isPinned && !isHidden;
  const isShown = isOpen || isDocked;
  const { dockWidth, assistantCornerOffset, assistantZIndex } = useLayout();

  useEffect(() => {
    if (isShown) {
      setHasBeenOpened(true);
    }
  }, [isShown]);

  // The page leaves this room at its right, so that the assistant does not cover it.
  useEffect(() => {
    if (!isDocked) {
      return;
    }

    const root = document.documentElement;
    root.style.setProperty('--assistant-dock', `${dockWidth}px`);

    return () => {
      root.style.removeProperty('--assistant-dock');
    };
  }, [isDocked, dockWidth]);

  const togglePin = () => {
    setPinned(!isPinned);
    setIsOpen(true);
  };

  const close = () => {
    setPinned(false);
    setIsOpen(false);
  };

  if (isHidden) {
    return null;
  }

  return (
    <>
      {!isShown && (
        <Fab
          aria-label={t('ASSISTANT_OPEN')}
          onClick={() => setIsOpen(true)}
          sx={(theme) => ({
            position: 'fixed',
            right: assistantCornerOffset,
            bottom: assistantCornerOffset,
            zIndex: assistantZIndex,
            color: 'primary.contrastText',
            background: gradient(theme),
            animation: hasBeenOpened ? undefined : `${pulse} 2.2s infinite`,
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.08)',
              background: gradient(theme),
            },
          })}
        >
          <AutoAwesomeIcon />
        </Fab>
      )}

      <AssistantPanel open={isShown} docked={isDocked}>
        <AssistantChat
          variant="window"
          isPinned={isDocked}
          onTogglePin={togglePin}
          onClose={close}
        />
      </AssistantPanel>
    </>
  );
}
